import { Form, InputNumber } from "../ui";
import { DatePicker } from "./ControlledPickers";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

const DAYS_PER_WEEK = 7;

/**
 * Props für die DatePickerWithDuration Komponente.
 * 
 * Diese Schnittstelle definiert alle verfügbaren Props für die DatePickerWithDuration Komponente.
 * Die meisten Props werden direkt an den zugrundeliegenden Ant Design DatePicker weitergegeben.
 */
export interface DatePickerWithDurationProps {
  /** 
   * Aktueller Wert des Datums (Dayjs-Objekt oder null).
   * Dies ist der Wert, der im DatePicker angezeigt wird und beim manuellen Ändern aktualisiert wird.
   */
  value?: Dayjs | null;
  
  /** 
   * Callback-Funktion, die aufgerufen wird, wenn sich das Datum ändert.
   * Wird sowohl bei manueller Auswahl im Kalender als auch bei automatischer Berechnung basierend auf Dauer aufgerufen.
   */
  onChange?: (date: Dayjs | null) => void;
  
  /** 
   * Startdatum für die Berechnung des Enddatums basierend auf Dauer (optional).
   * Wenn angegeben, wird das Enddatum automatisch berechnet, wenn Werte in den Dauer-Feldern eingegeben werden.
   * Priorität: Tage > Wochen > Monate
   */
  startDate?: Dayjs | null;
  
  /** 
   * Aktueller Wert für die Dauer in Tagen (number | null).
   * Wenn > 0, wird das Enddatum als Startdatum + diese Anzahl Tage berechnet.
   */
  durationDays?: number | null;
  
  /** 
   * Callback-Funktion, die aufgerufen wird, wenn sich der Wert für Tage ändert.
   * Der Wert wird automatisch auf null gesetzt, wenn 0 oder undefined eingegeben wird.
   */
  onDurationDaysChange?: (value: number | null) => void;
  
  /** 
   * Aktueller Wert für die Dauer in Wochen (number | null).
   * Wenn > 0 und keine Tage angegeben sind, wird das Enddatum als Startdatum + diese Anzahl Wochen berechnet.
   */
  durationWeeks?: number | null;
  
  /** 
   * Callback-Funktion, die aufgerufen wird, wenn sich der Wert für Wochen ändert.
   * Der Wert wird automatisch auf null gesetzt, wenn 0 oder undefined eingegeben wird.
   */
  onDurationWeeksChange?: (value: number | null) => void;
  
  /** 
   * Aktueller Wert für die Dauer in Monaten (number | null).
   * Wenn > 0 und keine Tage oder Wochen angegeben sind, wird das Enddatum als Startdatum + diese Anzahl Monate berechnet.
   */
  durationMonths?: number | null;

  /**
   * Wenn true, wird die Dauer als inklusiver Zeitraum interpretiert.
   * Beispiel: Start 01.01. + 1 Tag ergibt als Enddatum 01.01. statt 02.01.
   */
  inclusiveDurationEnd?: boolean;
  
  /** 
   * Callback-Funktion, die aufgerufen wird, wenn sich der Wert für Monate ändert.
   * Der Wert wird automatisch auf null gesetzt, wenn 0 oder undefined eingegeben wird.
   */
  onDurationMonthsChange?: (value: number | null) => void;
  
  /** 
   * Zeigt das Tage-Eingabefeld im Footer des DatePickers an (default: true).
   * Wenn false, wird das Feld nicht angezeigt, aber der Wert wird weiterhin berücksichtigt, falls vorhanden.
   */
  showDays?: boolean;
  
  /** 
   * Zeigt das Wochen-Eingabefeld im Footer des DatePickers an (default: true).
   * Wenn false, wird das Feld nicht angezeigt, aber der Wert wird weiterhin berücksichtigt, falls vorhanden.
   */
  showWeeks?: boolean;
  
  /** 
   * Zeigt das Monate-Eingabefeld im Footer des DatePickers an (default: true).
   * Wenn false, wird das Feld nicht angezeigt, aber der Wert wird weiterhin berücksichtigt, falls vorhanden.
   */
  showMonths?: boolean;
  
  /** 
   * Datumsformat für die Anzeige (optional; Standard: Konten-Locale wie bei formatDateOnly).
   * Wird direkt an den Ant Design DatePicker weitergegeben.
   */
  format?: string | string[];

  /**
   * Übersetzungsfunktion der nutzenden App.
   * Erwartete Keys: "Days", "Weeks", "Months", "Select date".
   */
  text?: (key: string) => string;
  
  /** 
   * Platzhaltertext für das DatePicker-Input-Feld.
   * Wenn nicht angegeben, wird ein Standard-Text verwendet (i18n-abhängig).
   */
  placeholder?: string;
  
  /** 
   * CSS-Styles für das DatePicker-Input-Feld.
   * Default: { width: "100%" }
   */
  style?: React.CSSProperties;
  
  /** 
   * Erlaubt das Löschen des Datums durch Klick auf das X-Symbol (default: false).
   * Wird direkt an den Ant Design DatePicker weitergegeben.
   */
  allowClear?: boolean;
  
  /** 
   * CSS-Styles für das Popup/Dropdown des DatePickers.
   * Default: { width: "280px", overflow: "hidden" }
   */
  popupStyle?: React.CSSProperties;
  
  /** 
   * Deaktiviert das DatePicker (default: false).
   * Wird direkt an den Ant Design DatePicker weitergegeben.
   */
  disabled?: boolean;
  
  /**
   * Zusätzliche CSS-Klasse für das Dropdown-Popup (optional).
   * Intern als `classNames.popup.root` an den DatePicker übergeben (Ant Design 6).
   */
  dropdownClassName?: string;
  
  /** 
   * Weitere Props, die direkt an den Ant Design DatePicker weitergegeben werden.
   * Dies ermöglicht die Verwendung aller Standard-DatePicker-Props.
   */
  [key: string]: any;
}

/**
 * DatePickerWithDuration - Erweiterter DatePicker mit optionalen Dauer-Eingabefeldern
 * 
 * Diese Komponente erweitert den Standard Ant Design DatePicker um die Möglichkeit,
 * ein Enddatum entweder manuell auszuwählen oder durch Eingabe einer Dauer (Tage, Wochen, Monate)
 * relativ zu einem Startdatum zu berechnen.
 * 
 * **Verwendung:**
 * 
 * ```tsx
 * // Beispiel 1: Mit Ant Design Form.Item (empfohlen)
 * import { Form } from "antd";
 * import { DatePickerWithDuration } from "./components/DatePickerWithDuration";
 * 
 * const [form] = Form.useForm();
 * const startDate = Form.useWatch("startDate", form);
 * const endDate = Form.useWatch("endDate", form);
 * const durationDays = Form.useWatch("durationDays", form);
 * const durationWeeks = Form.useWatch("durationWeeks", form);
 * const durationMonths = Form.useWatch("durationMonths", form);
 * 
 * <Form form={form}>
 *   <Form.Item name="startDate" label="Startdatum">
 *     <DatePicker format="DD.MM.YYYY" />
 *   </Form.Item>
 *   
 *   <Form.Item name="endDate" label="Enddatum">
 *     <DatePickerWithDuration
 *       value={endDate}
 *       onChange={(date) => form.setFieldValue("endDate", date)}
 *       startDate={startDate}
 *       durationDays={durationDays}
 *       onDurationDaysChange={(val) => form.setFieldValue("durationDays", val)}
 *       durationWeeks={durationWeeks}
 *       onDurationWeeksChange={(val) => form.setFieldValue("durationWeeks", val)}
 *       durationMonths={durationMonths}
 *       onDurationMonthsChange={(val) => form.setFieldValue("durationMonths", val)}
 *       format="DD.MM.YYYY"
 *     />
 *   </Form.Item>
 * </Form>
 * 
 * // Beispiel 2: Mit allen Dauer-Feldern (Standard, ohne Form)
 * <DatePickerWithDuration
 *   value={endDate}
 *   onChange={(date) => setEndDate(date)}
 *   startDate={startDate}
 *   durationDays={days}
 *   onDurationDaysChange={(val) => setDays(val)}
 *   durationWeeks={weeks}
 *   onDurationWeeksChange={(val) => setWeeks(val)}
 *   durationMonths={months}
 *   onDurationMonthsChange={(val) => setMonths(val)}
 * />
 * 
 * // Beispiel 3: Nur Tage und Wochen anzeigen
 * <DatePickerWithDuration
 *   value={endDate}
 *   onChange={(date) => setEndDate(date)}
 *   startDate={startDate}
 *   durationDays={days}
 *   onDurationDaysChange={(val) => setDays(val)}
 *   durationWeeks={weeks}
 *   onDurationWeeksChange={(val) => setWeeks(val)}
 *   showMonths={false}
 * />
 * 
 * // Beispiel 4: Ohne Startdatum (nur manuelle Datumsauswahl, Standard-DatePicker)
 * <DatePickerWithDuration
 *   value={date}
 *   onChange={(date) => setDate(date)}
 *   showDays={false}
 *   showWeeks={false}
 *   showMonths={false}
 * />
 * ```
 * 
 * **Funktionsweise:**
 * 
 * 1. **Automatische Berechnung**: Wenn ein `startDate` und eine Dauer (Tage/Wochen/Monate) angegeben wird,
 *    wird das Enddatum automatisch berechnet und der `onChange`-Callback wird aufgerufen.
 *    Priorität: Tage > Wochen > Monate (nur das erste gefüllte Feld wird verwendet).
 * 
 * 2. **Manuelle Datumsauswahl**: Der Benutzer kann das Datum auch direkt im Kalender auswählen.
 *    Wenn das manuell gewählte Datum von der berechneten Dauer abweicht, werden die Dauer-Felder gelöscht.
 * 
 * 3. **DatePicker-Navigation**: Wenn eine Dauer eingegeben wird, springt der DatePicker automatisch
 *    zum berechneten Datum, damit der Benutzer sieht, welches Datum ausgewählt wird.
 * 
 * 4. **Zurücksetzen**: Wenn alle Dauer-Felder geleert werden (0 oder null), springt der DatePicker
 *    zurück zum ursprünglich im `value` gespeicherten Datum.
 * 
 * **Technische Details:**
 * - Die Dauer-Felder werden im Footer des DatePicker-Popups angezeigt (renderExtraFooter).
 * - Die Komponente verwendet interne State-Verwaltung für `pickerValue`, um die Kalenderansicht zu steuern.
 * - Ein `isSettingDateProgrammatically` Flag verhindert Endlosschleifen beim programmatischen Setzen des Datums.
 * - Die Berechnung erfolgt mit dayjs: `startDate.add(days, "day")` / `startDate.add(weeks * DAYS_PER_WEEK, "day")` / `startDate.add(months, "month")`.
 * 
 * @param props - Die Props für die DatePickerWithDuration Komponente
 * @returns Ein React-Element mit dem erweiterten DatePicker
 */

export function DatePickerWithDuration({
  value,
  onChange,
  startDate,
  durationDays,
  onDurationDaysChange,
  durationWeeks,
  onDurationWeeksChange,
  durationMonths,
  inclusiveDurationEnd = false,
  onDurationMonthsChange,
  showDays = true,
  showWeeks = true,
  showMonths = true,
  format: formatFromProps,
  text = (key) => key,
  placeholder,
  style,
  allowClear = true,
  // Keine feste Breite erzwingen: antd dimensioniert das Date-Panel natürlich (288px),
  // der Duration-Footer ist schmaler. Eine feste Breite würde das Grid wieder verschieben.
  popupStyle,
  disabled,
  dropdownClassName,
  ...restProps
}: DatePickerWithDurationProps) {
  const format = formatFromProps ?? "DD.MM.YYYY";
  
  // Flag um zu verhindern, dass onChange beim programmatischen Setzen des Datums feuert
  const isSettingDateProgrammatically = React.useRef(false);
  
  // Lokale State-Variablen für die Dauer-Werte (werden sofort aktualisiert, ohne auf Parent zu warten)
  // Diese werden mit den Props synchronisiert, aber ermöglichen sofortige Berechnung beim Tippen
  const [localDays, setLocalDays] = React.useState<number | null>(durationDays ?? null);
  const [localWeeks, setLocalWeeks] = React.useState<number | null>(durationWeeks ?? null);
  const [localMonths, setLocalMonths] = React.useState<number | null>(durationMonths ?? null);
  
  // Synchronisiere lokale State mit Props (wenn Props sich von außen ändern)
  React.useEffect(() => {
    setLocalDays(durationDays ?? null);
  }, [durationDays]);
  React.useEffect(() => {
    setLocalWeeks(durationWeeks ?? null);
  }, [durationWeeks]);
  React.useEffect(() => {
    setLocalMonths(durationMonths ?? null);
  }, [durationMonths]);
  
  // State für manuelle Navigation im Kalender (nur wenn KEINE Dauer angegeben ist)
  const [manualPickerValue, setManualPickerValue] = React.useState<Dayjs | null>(null);

  const calculateDurationEndDate = React.useCallback(
    (start: Dayjs, days: number, weeks: number, months: number) => {
      let calculatedDate = start;
      if (days > 0) calculatedDate = calculatedDate.add(days, "day");
      if (weeks > 0) calculatedDate = calculatedDate.add(weeks * DAYS_PER_WEEK, "day");
      if (months > 0) calculatedDate = calculatedDate.add(months, "month");
      return inclusiveDurationEnd && (days > 0 || weeks > 0 || months > 0)
        ? calculatedDate.add(-1, "day")
        : calculatedDate;
    },
    [inclusiveDurationEnd]
  );
  
  // Berechne den effektiven pickerValue synchron über useMemo
  // Das stellt sicher, dass der DatePicker sofort zum richtigen Datum springt
  const calculatedPickerValue = React.useMemo(() => {
    if (startDate) {
      const start = dayjs(startDate);
      if (start.isValid()) {
        const days = durationDays != null && durationDays > 0 ? Number(durationDays) : 0;
        const weeks = durationWeeks != null && durationWeeks > 0 ? Number(durationWeeks) : 0;
        const months = durationMonths != null && durationMonths > 0 ? Number(durationMonths) : 0;
        
        if (days > 0 || weeks > 0 || months > 0) {
          return calculateDurationEndDate(start, days, weeks, months);
        }
      }
    }
    return null;
  }, [calculateDurationEndDate, startDate, durationDays, durationWeeks, durationMonths]);
  
  // Der effektive pickerValue: manuell (für sofortigen Sprung) oder berechnet oder value
  const effectivePickerValue = React.useMemo(() => {
    // manualPickerValue hat Priorität (wird für sofortigen Sprung beim Tippen verwendet)
    if (manualPickerValue) {
      return manualPickerValue;
    }
    // Wenn eine Dauer berechnet wurde, verwende diese
    if (calculatedPickerValue) {
      return calculatedPickerValue;
    }
    // Fallback: aktueller Wert aus dem Textfeld
    if (value && dayjs(value).isValid()) {
      return dayjs(value);
    }
    return null;
  }, [calculatedPickerValue, manualPickerValue, value]);
  
  // Hilfsfunktion: Berechne das Datum und setze es sofort als value
  // Wird direkt im onChange der InputNumber aufgerufen, BEVOR die Props aktualisiert werden
  // Der DatePicker springt automatisch zum Monat des ausgewählten Datums
  const updateDateFromDuration = React.useCallback((newDays: number | null, newWeeks: number | null, newMonths: number | null) => {
    if (!startDate || !onChange) return;
    
    const start = dayjs(startDate);
    if (!start.isValid()) return;
    
    const days = newDays != null && newDays > 0 ? Number(newDays) : 0;
    const weeks = newWeeks != null && newWeeks > 0 ? Number(newWeeks) : 0;
    const months = newMonths != null && newMonths > 0 ? Number(newMonths) : 0;
    
    // Berechne das Datum (auch wenn alle Werte 0 sind → dann ist es das Startdatum)
    const calculatedDate = calculateDurationEndDate(start, days, weeks, months);
    
    // Setze das berechnete Datum direkt als value
    // Der DatePicker navigiert automatisch zum Monat des ausgewählten Datums
    isSettingDateProgrammatically.current = true;
    onChange(calculatedDate);
    setTimeout(() => {
      isSettingDateProgrammatically.current = false;
    }, 0);
  }, [calculateDurationEndDate, startDate, onChange]);
  
  // Setze manualPickerValue zurück, wenn sich value ändert (aber NICHT bei Dauer-Änderungen,
  // weil wir manualPickerValue für den sofortigen Sprung beim Tippen verwenden)
  React.useEffect(() => {
    setManualPickerValue(null);
  }, [value]);
  
  // Separater useEffect für die Aktualisierung des value durch onChange (aktualisiert das angebundene Textfeld)
  React.useEffect(() => {
    if (!startDate || !onChange) return;
    
    const start = dayjs(startDate);
    if (!start.isValid()) return;
    
    // Prüfe alle 3 Felder: Alle Werte werden addiert
    const days = durationDays != null && durationDays > 0 ? Number(durationDays) : 0;
    const weeks = durationWeeks != null && durationWeeks > 0 ? Number(durationWeeks) : 0;
    const months = durationMonths != null && durationMonths > 0 ? Number(durationMonths) : 0;
    
    // Nur aktualisieren, wenn eine Dauer angegeben wurde
    if (days > 0 || weeks > 0 || months > 0) {
      // Berechne das Datum durch Addition aller Werte
      const calculatedDate = calculateDurationEndDate(start, days, weeks, months);
      
      if (calculatedDate && calculatedDate.isValid()) {
        const currentValue = value ? dayjs(value) : null;
        // Aktualisiere nur, wenn sich das berechnete Datum unterscheidet
        if (!currentValue || !currentValue.isSame(calculatedDate, "day")) {
          isSettingDateProgrammatically.current = true;
          onChange(calculatedDate);
          // Setze Flag nach kurzer Verzögerung zurück
          setTimeout(() => {
            isSettingDateProgrammatically.current = false;
          }, 0);
        }
      }
    }
    // Wenn keine Dauer angegeben ist, bleibt value unverändert (wird vom Benutzer über Textfeld/DatePicker gesetzt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDurationEndDate, startDate, durationDays, durationWeeks, durationMonths, onChange]); // value bewusst NICHT in Abhängigkeiten, um Endlosschleifen zu vermeiden
  
  
  const handleDateChange = (date: Dayjs | null) => {
    // Ignoriere onChange wenn das Datum programmatisch gesetzt wurde
    if (isSettingDateProgrammatically.current) {
      return;
    }
    
    // Wenn Datum manuell geändert wird und Startdatum vorhanden ist, lösche Dauer-Felder wenn nötig
    if (date && startDate) {
      const start = dayjs(startDate);
      if (start.isValid()) {
        const days = durationDays != null && durationDays > 0 ? Number(durationDays) : 0;
        const weeks = durationWeeks != null && durationWeeks > 0 ? Number(durationWeeks) : 0;
        const months = durationMonths != null && durationMonths > 0 ? Number(durationMonths) : 0;
        
        // Berechne das Datum durch Addition aller Werte
        let calculatedDate: Dayjs | null = null;
        if (days > 0 || weeks > 0 || months > 0) {
          calculatedDate = calculateDurationEndDate(start, days, weeks, months);
        }
        
        // Wenn ein berechnetes Datum existiert und das manuell gewählte Datum davon abweicht,
        // lösche die Dauer-Felder
        if (calculatedDate && !dayjs(date).isSame(calculatedDate, "day")) {
          if (onDurationDaysChange) onDurationDaysChange(null);
          if (onDurationWeeksChange) onDurationWeeksChange(null);
          if (onDurationMonthsChange) onDurationMonthsChange(null);
        }
      }
    } else if (!date) {
      // Wenn das Datum gelöscht wird, lösche auch die Dauer-Felder
      if (onDurationDaysChange) onDurationDaysChange(null);
      if (onDurationWeeksChange) onDurationWeeksChange(null);
      if (onDurationMonthsChange) onDurationMonthsChange(null);
    }
    
    if (onChange) {
      onChange(date);
    }
  };
  
  const handlePickerValueChange = (date: Dayjs | null) => {
    // Wenn keine Dauer angegeben ist, erlaube manuelle Navigation
    const days = durationDays != null && durationDays > 0 ? Number(durationDays) : 0;
    const weeks = durationWeeks != null && durationWeeks > 0 ? Number(durationWeeks) : 0;
    const months = durationMonths != null && durationMonths > 0 ? Number(durationMonths) : 0;
    const currentHasDuration = days > 0 || weeks > 0 || months > 0;
    
    if (!currentHasDuration && date) {
      setManualPickerValue(date);
    }
    // Wenn Dauer vorhanden ist, ignoriere manuelle Navigation - pickerValue wird durch useMemo berechnet
  };
  
  // Kein erzwungenes width-Fallback mehr: das Popup orientiert sich an antds natürlicher Panel-Breite (288px).
  const effectivePopupStyle = popupStyle ?? {};

  const {
    popupStyle: _restPopupStyle,
    dropdownClassName: restDropdownClassName,
    classNames: restClassNames,
    styles: restStyles,
    ...restPropsTail
  } = restProps;

  const popupRootClassName = ["date-picker-with-duration", dropdownClassName, restDropdownClassName]
    .filter((x): x is string => Boolean(x && String(x).trim()))
    .join(" ");

  const mergedClassNames = React.useMemo(() => {
    if (!restClassNames) {
      return { popup: { root: popupRootClassName } };
    }
    const p = restClassNames.popup;
    if (typeof p === "string") {
      return { ...restClassNames, popup: { root: `${p} ${popupRootClassName}`.trim() } };
    }
    if (p && typeof p === "object") {
      return {
        ...restClassNames,
        popup: { ...p, root: [p.root, popupRootClassName].filter(Boolean).join(" ") },
      };
    }
    return { ...restClassNames, popup: { root: popupRootClassName } };
  }, [restClassNames, popupRootClassName]);

  const mergedStyles = React.useMemo(() => {
    const fromRestPopup = restStyles?.popup;
    const existingRoot =
      fromRestPopup && typeof fromRestPopup === "object" && fromRestPopup !== null && "root" in fromRestPopup
        ? (fromRestPopup as { root?: React.CSSProperties }).root
        : undefined;
    return {
      ...restStyles,
      popup: {
        ...(typeof fromRestPopup === "object" && fromRestPopup ? fromRestPopup : {}),
        root: { ...(existingRoot && typeof existingRoot === "object" ? existingRoot : {}), ...effectivePopupStyle },
      },
    };
  }, [restStyles, effectivePopupStyle]);
  
  // Erstelle die Dauer-Felder basierend auf den show-Flags
  const durationFields: React.ReactNode[] = [];
  
  if (showDays) {
    durationFields.push(
      <Form.Item key="days" noStyle style={{ marginBottom: 0, flex: "1 1 0", minWidth: 0 }}>
        <InputNumber 
          min={0} 
          step={1}
          placeholder={text("Days")}
          style={{ width: "100%" }}
          size="small"
          value={localDays ?? undefined}
          onChange={(val) => {
            // Konvertiere zu null wenn 0 oder undefined
            const numValue = val != null && val > 0 ? Number(val) : null;
            // Aktualisiere lokalen State sofort
            setLocalDays(numValue);
            // Setze das berechnete Datum sofort als value (verwendet lokale Werte für alle 3 Felder)
            updateDateFromDuration(numValue, localWeeks, localMonths);
            // Benachrichtige den Parent
            if (onDurationDaysChange) {
              onDurationDaysChange(numValue);
            }
          }}
        />
      </Form.Item>
    );
  }
  
  if (showWeeks) {
    durationFields.push(
      <Form.Item key="weeks" noStyle style={{ marginBottom: 0, flex: "1 1 0", minWidth: 0 }}>
        <InputNumber 
          min={0} 
          step={1}
          placeholder={text("Weeks")}
          style={{ width: "100%" }}
          size="small"
          value={localWeeks ?? undefined}
          onChange={(val) => {
            // Konvertiere zu null wenn 0 oder undefined
            const numValue = val != null && val > 0 ? Number(val) : null;
            // Aktualisiere lokalen State sofort
            setLocalWeeks(numValue);
            // Setze das berechnete Datum sofort als value (verwendet lokale Werte für alle 3 Felder)
            updateDateFromDuration(localDays, numValue, localMonths);
            // Benachrichtige den Parent
            if (onDurationWeeksChange) {
              onDurationWeeksChange(numValue);
            }
          }}
        />
      </Form.Item>
    );
  }
  
  if (showMonths) {
    durationFields.push(
      <Form.Item key="months" noStyle style={{ marginBottom: 0, flex: "1 1 0", minWidth: 0 }}>
        <InputNumber 
          min={0} 
          step={1}
          placeholder={text("Months")}
          style={{ width: "100%" }}
          size="small"
          value={localMonths ?? undefined}
          onChange={(val) => {
            // Konvertiere zu null wenn 0 oder undefined
            const numValue = val != null && val > 0 ? Number(val) : null;
            // Aktualisiere lokalen State sofort
            setLocalMonths(numValue);
            // Setze das berechnete Datum sofort als value (verwendet lokale Werte für alle 3 Felder)
            updateDateFromDuration(localDays, localWeeks, numValue);
            // Benachrichtige den Parent
            if (onDurationMonthsChange) {
              onDurationMonthsChange(numValue);
            }
          }}
        />
      </Form.Item>
    );
  }
  
  return (
    <DatePicker
      value={value}
      onChange={handleDateChange}
      allowClear={allowClear}
      format={format}
      style={style ?? { width: "100%" }}
      placeholder={placeholder ?? text("Select date")}
      pickerValue={effectivePickerValue ?? undefined}
      onPickerValueChange={handlePickerValueChange}
      disabled={disabled}
      renderExtraFooter={
        durationFields.length > 0
          ? () => (
              <div className="date-picker-duration-footer" style={{ 
                padding: "calc(var(--mq-space-sm) / 2) var(--mq-space-sm)",
                margin: "0",
                boxSizing: "border-box", 
                width: "100%",
                display: "flex",
                gap: "var(--mq-space-xxs)",
                justifyContent: "center",
                alignItems: "center"
              }}>
                {durationFields}
              </div>
            )
          : undefined
      }
      {...restPropsTail}
      classNames={mergedClassNames}
      styles={mergedStyles}
    />
  );
}
